/**
 * Nutricode focus card mid — SVG charts for Shopify (and any host).
 *
 * All 8 chart types render into a shared viewBox (NFC_VB_W x NFC_VB_H) with a
 * shared plot rectangle. The left gutter is computed from the widest Y-tick
 * label so every chart lines up at the same plotLeft regardless of the value
 * domain being shown. The CSS panel owns the aspect ratio; the SVG stretches
 * to fill it with preserveAspectRatio="none" (panel aspect == viewBox aspect,
 * so scaling stays uniform in both axes).
 *
 * Typography is driven entirely by base-new.css font classes
 * (font-jetbrains-mono, font-jetbrains-mono-10px, body-regular-grey,
 * body-medium-grey). No font-size / font-weight / fill / font-family /
 * letter-spacing attributes are written onto <text> elements — role classes
 * below are the single source of truth.
 *
 * @global NutricodeFocusCharts.mount(container, options)
 */

function nutricodeEscAttr(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
}

/** Safe fragment for SVG / filter ids when multiple cards share a page */
function nutricodeSafeIdSuffix(domId) {
  const x = String(domId || 'card').replace(/[^a-zA-Z0-9_-]/g, '_');
  return x.match(/^[a-zA-Z_]/) ? x : 'nfc_' + x;
}

function nutricodeEscapeHtmlText(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Formats a duration given in seconds for axis ticks / point labels (not raw seconds).
 * Under 1 hour: "42m". One hour or more: "1h30" (hours + two-digit minutes). Under one minute: "0m".
 */
function nfcFormatSecondsAxis(sec) {
  const s = Math.max(0, +sec);
  if (!Number.isFinite(s)) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) {
    return `${h}h${String(m).padStart(2, '0')}`;
  }
  return `${m}m`;
}

/* ==================================================================== */
/* Shared viewBox, plot rect, typography                                 */
/* ==================================================================== */

/** ViewBox width / height for every chart — keep in sync with CSS `aspect-ratio` on `.nutricode-focus-card-mid__chart-panel`. */
const NFC_VB_W = 360;
const NFC_VB_H = 190;

/**
 * Padding inside the viewBox for the plot rectangle. These provide the visual
 * breathing room between the plot area and the panel edges (the panel itself
 * has no CSS padding, so this is the only buffer). Bumped from the original
 * 10u after panel padding was removed, to restore a comfortable edge gap at
 * the reference panel width (360 CSS px → 14 CSS px of breathing room).
 */
const NFC_PAD_TOP = 30;
const NFC_PAD_RIGHT = 20;
const NFC_PAD_LEFT_MIN = 20;
/**
 * Room below the plot for x-tick labels. Must be ≥
 *   NFC_XTICK_BELOW_PLOT + descender(maxFontUnits)
 * so the tick baselines (plot.bottom + NFC_XTICK_BELOW_PLOT) don't overflow
 * the viewBox bottom when the responsive font-size binder scales the
 * user-unit font size up on narrow panels.
 */
const NFC_PAD_BOTTOM_AXIS = 34;
/**
 * Room below the plot when an x-axis title is also drawn (scatter / tier-5).
 * Must be ≥ NFC_XTITLE_BELOW_PLOT + descender(maxFontUnits). Bumped above
 * the naive "28 + 4" to cover the NFC_TARGET_CSS_PX_MIN clamp case on very
 * narrow phones (where font-user-units can exceed 14).
 */
const NFC_PAD_BOTTOM_WITH_XTITLE = 42;
/**
 * Horizontal band reserved left of the y-tick labels for a rotated y-axis
 * title (scatter + tier-5). This is the total extra gutter added on top of
 * NFC_PAD_LEFT_MIN, and it must cover (a) the width of the rotated title
 * band (~1 em), (b) a visible gap between the title and the tick labels.
 */
const NFC_AXIS_TITLE_GUTTER = 24;
/** Vertical offset from plot.bottom to the x-tick label baseline. */
const NFC_XTICK_BELOW_PLOT = 12;
/** Vertical offset from plot.bottom to the x-axis title baseline. */
const NFC_XTITLE_BELOW_PLOT = 32;
/** Gap between the right edge of y-tick labels and the plot. */
const NFC_GUTTER_GAP = 6;
/** Font size assumed for width estimates — must match the CSS .font-*-10px rules. */
const NFC_TICK_FONT_PX = 10;
/** Narrow-monospace digit average. Generous for "ms"/"/h"/"%" suffixes. */
const NFC_CHAR_WIDTH_FACTOR = 0.6;

/** Role classes defined in assets/base-new.css. Every <text> uses exactly one. */
const NFC_CLS_TICK = 'font-jetbrains-mono font-jetbrains-mono-10px body-regular-lightgrey';
const NFC_CLS_AXIS_TITLE = 'font-jetbrains-mono font-jetbrains-mono-10px body-medium-grey';

/**
 * SVG text size fix: CSS `font-size: 10px` inside an SVG resolves in user
 * units (viewBox), not CSS pixels. With preserveAspectRatio="none" stretching
 * a 360×190 viewBox to a panel wider than 360 CSS px, "10px" becomes 10 user
 * units -> many CSS pixels on screen. This binder measures the rendered SVG
 * size and writes an inline font-size (in user units) on each <text> so the
 * glyphs actually land at a target CSS-pixel size on screen. Inline style
 * beats the .font-*-10px class rule from base-new.css.
 *
 * Responsive target:
 *   The target CSS px scales with the panel width so text naturally shrinks
 *   on narrow panels / smaller screens. We aim for 10 CSS px at the reference
 *   panel width (NFC_TARGET_REF_PANEL_W), drop linearly down to
 *   NFC_TARGET_CSS_PX_MIN below that, and cap at NFC_TARGET_CSS_PX_MAX above
 *   a second reference width so desktops don't get over-large tick text.
 *
 * Layout note: plotLeft gutters still use NFC_TICK_FONT_PX=10 user units as
 * their nominal width. At panels wider than NFC_VB_W the reserved gutter is
 * a bit generous (extra whitespace). At panels narrower than NFC_VB_W the
 * gutter may be tight; the panel's `overflow: hidden` clips any minor spill.
 */
const NFC_TARGET_CSS_PX_MIN = 7;
const NFC_TARGET_CSS_PX_MAX = 10;
const NFC_TARGET_REF_PANEL_W = 360;
const __nfcFontPxBindings = new WeakMap();

function nfcComputeTargetCssPx(panelWidthCssPx) {
  const w = +panelWidthCssPx;
  if (!(w > 0)) return NFC_TARGET_CSS_PX_MAX;
  if (w >= NFC_TARGET_REF_PANEL_W) return NFC_TARGET_CSS_PX_MAX;
  const scaled = (w / NFC_TARGET_REF_PANEL_W) * NFC_TARGET_CSS_PX_MAX;
  return Math.max(NFC_TARGET_CSS_PX_MIN, scaled);
}

function nfcBindFontPx(svg, targetCssPx) {
  if (!svg || svg.tagName !== 'svg') return;
  const explicitTarget = targetCssPx == null ? null : +targetCssPx;
  const prev = __nfcFontPxBindings.get(svg);
  if (prev) {
    if (prev.ro) prev.ro.disconnect();
    if (prev.onWin) window.removeEventListener('resize', prev.onWin);
    if (prev.state && prev.state.raf) cancelAnimationFrame(prev.state.raf);
  }
  const state = { raf: 0 };
  const sync = () => {
    state.raf = 0;
    const vb = svg.viewBox && svg.viewBox.baseVal;
    if (!vb || !(vb.width > 0) || !(vb.height > 0)) return;
    const r = svg.getBoundingClientRect();
    if (!(r.width > 0) || !(r.height > 0)) return;
    const uPerCssPx = (vb.width / r.width + vb.height / r.height) / 2;
    const target = explicitTarget != null ? explicitTarget : nfcComputeTargetCssPx(r.width);
    const fs = Math.max(0.01, target * uPerCssPx);
    const fsStr = (+fs.toFixed(3)).toString() + 'px';
    svg.querySelectorAll('text').forEach((t) => {
      t.style.fontSize = fsStr;
    });
    /*
     * After the real font-size is applied, fit any axis titles tagged with
     * data-nfc-fit-max. We measure the natural width (post-font-size, pre-
     * textLength) via getBBox and only compress glyphs when the text would
     * actually exceed the allowed budget along its baseline direction.
     * This avoids the visible "stretched" look that came from using a
     * pessimistic static font-size estimate to decide compression ahead of
     * time.
     */
    svg.querySelectorAll('text[data-nfc-fit-max]').forEach((t) => {
      const max = parseFloat(t.getAttribute('data-nfc-fit-max'));
      if (!(max > 0)) return;
      t.removeAttribute('textLength');
      t.removeAttribute('lengthAdjust');
      let bbox = null;
      try { bbox = t.getBBox(); } catch (e) { return; }
      if (!bbox || !(bbox.width > 0)) return;
      if (bbox.width > max) {
        t.setAttribute('textLength', max.toFixed(2));
        t.setAttribute('lengthAdjust', 'spacingAndGlyphs');
      }
    });
  };
  const schedule = () => {
    if (state.raf) cancelAnimationFrame(state.raf);
    state.raf = requestAnimationFrame(sync);
  };
  const onWin = () => schedule();
  window.addEventListener('resize', onWin, { passive: true });
  let ro = null;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(schedule);
    ro.observe(svg);
  }
  __nfcFontPxBindings.set(svg, { ro, onWin, state });
  schedule();
  requestAnimationFrame(() => requestAnimationFrame(sync));
}

function nfcTextWidth(text, fontPx) {
  const px = fontPx == null ? NFC_TICK_FONT_PX : fontPx;
  return String(text == null ? '' : text).length * NFC_CHAR_WIDTH_FACTOR * px;
}

/**
 * From an array of y-tick label strings, compute where Y labels should sit
 * (end-anchored at yLabelX) and where the plot starts (plotLeft).
 * axisTitleExtra reserves extra horizontal space for a rotated Y-axis title.
 */
function nfcYAxisGutter(labels, fontPx, axisTitleExtra) {
  const px = fontPx == null ? NFC_TICK_FONT_PX : fontPx;
  const extra = axisTitleExtra == null ? 0 : +axisTitleExtra;
  let maxW = 0;
  for (let i = 0; i < labels.length; i++) {
    const w = nfcTextWidth(labels[i], px);
    if (w > maxW) maxW = w;
  }
  const yLabelX = NFC_PAD_LEFT_MIN + extra + maxW;
  return { yLabelX, plotLeft: yLabelX + NFC_GUTTER_GAP, axisTitleExtra: extra };
}

/**
 * Marks an axis-title <text> element with the maximum user-unit length it is
 * allowed to occupy along its baseline direction (for rotated y-titles this
 * is the plot height; for x-titles, the plot width). The `nfcBindFontPx`
 * pass reads this attribute after setting the real font size, measures the
 * text's *actual* rendered width via getBBox(), and only then decides
 * whether to apply `textLength` + `lengthAdjust="spacingAndGlyphs"` to
 * squeeze the glyphs into `maxUnits`.
 *
 * This replaces a previous static-estimate approach that used a pessimistic
 * font-size to guess whether the title would overflow. The static estimate
 * over-triggered compression at normal panel widths (where the actual font
 * is ~10 user units) because it assumed the worst-case narrow-phone font
 * (~14 user units), visibly squishing long rotated titles like "Longevity
 * Expectation" even when they had room to render naturally.
 */
function nfcAxisTitleAttrs(_text, maxUnits) {
  if (!(maxUnits > 0)) return '';
  return ` data-nfc-fit-max="${(+maxUnits).toFixed(2)}"`;
}

/** Plot rectangle derived from a computed plotLeft + optional bottom padding. */
function nfcPlotRect(plotLeft, bottomPadOverride) {
  const padBottom = bottomPadOverride == null ? NFC_PAD_BOTTOM_AXIS : +bottomPadOverride;
  const plotTop = NFC_PAD_TOP;
  const plotBottom = NFC_VB_H - padBottom;
  const plotRight = NFC_VB_W - NFC_PAD_RIGHT;
  return {
    plotLeft,
    plotRight,
    plotTop,
    plotBottom,
    plotW: plotRight - plotLeft,
    plotH: plotBottom - plotTop,
  };
}

/* ==================================================================== */
/* Chart renderers                                                        */
/* ==================================================================== */

function nutricodeDrawFocusRem(svg, data, slotSuffix) {
  void slotSuffix;
  const ageToRemBracket = age => {
    if (age == null || !Number.isFinite(+age)) return '26-35';
    const a = +age;
    if (a <= 25) return '18-25';
    if (a <= 35) return '26-35';
    if (a <= 45) return '36-45';
    if (a <= 55) return '46-55';
    if (a <= 65) return '56-65';
    return '66+';
  };
  void ageToRemBracket;

  const deriveRemPercentilesFromP50 = p50 => {
    const p50n = +p50;
    if (!Number.isFinite(p50n)) return { p50: 21, p30: 18.2, p60: 22.2, p90: 26.0 };
    return {
      p50: p50n,
      p30: Math.max(5, Math.round((p50n - 2.8) * 10) / 10),
      p60: Math.min(50, Math.round((p50n + 1.2) * 10) / 10),
      p90: Math.min(50, Math.round((p50n + 5.0) * 10) / 10),
    };
  };

  const remFillForCohortPct = (pct, p30, p60, p90, lowerIsBetter) => {
    if (lowerIsBetter) {
      if (pct <= p30) return '#5da9c8';
      if (pct <= p60) return '#34c759';
      if (pct <= p90) return '#ff9f0a';
      return '#ff0000';
    }
    if (pct < p30) return '#ff0000';
    if (pct < p60) return '#ff9f0a';
    if (pct < p90) return '#34c759';
    return '#5da9c8';
  };

  const remBandDesc = (pct, p30, p60, p90, lowerIsBetter) => {
    if (lowerIsBetter) {
      if (pct <= p30) return 'top cohort range';
      if (pct <= p60) return 'good cohort range';
      if (pct <= p90) return 'below median cohort range';
      return 'lowest cohort range';
    }
    if (pct < p30) return '0–30th percentile vs cohort';
    if (pct < p60) return '30–60th percentile vs cohort';
    if (pct < p90) return '60–90th percentile vs cohort';
    return '90th percentile or above vs cohort';
  };

  const niceStepPercent = (range, targetTicks) => {
    const rough = Math.max(range, 0.0001) / Math.max(1, targetTicks - 1);
    const pow = 10 ** Math.floor(Math.log10(rough));
    const err = rough / pow;
    const f = err <= 1 ? 1 : err <= 2 ? 2 : err <= 2.5 ? 2.5 : err <= 5 ? 5 : 10;
    return f * pow;
  };

  const seed = s => {
    let x = s;
    return () => {
      x = Math.sin(x) * 10000;
      return x - Math.floor(x);
    };
  };

  const _fc1 = data;
  const metricLabel = String(_fc1.metricLabel || 'REM sleep');
  const baselineLabel = String(_fc1.baselineLabel || 'demographic average');
  const unitLabel = String(_fc1.unitLabel || _fc1.yUnit || '%');
  const lowerIsBetter = Boolean(_fc1.lowerIsBetter);
  let numDays = Math.max(1, Math.min(31, +_fc1.numDays || 30));
  let dailyPct = Array.isArray(_fc1.dailyPct) ? _fc1.dailyPct.slice() : [];
  let remPctiles = _fc1.remPctiles && typeof _fc1.remPctiles === 'object'
    ? { ...deriveRemPercentilesFromP50(21), ..._fc1.remPctiles }
    : deriveRemPercentilesFromP50(21);
  let demographicPct = +_fc1.demographicPct || remPctiles.p50;
  const metaGender = _fc1.metaGender === 'female' ? 'female' : 'male';
  let metaAge = +_fc1.metaAge;
  if (!Number.isFinite(metaAge)) metaAge = 30;

  let validPct = dailyPct.filter(v => v != null && Number.isFinite(v));
  if (validPct.length < 2) {
    const rng = seed(19);
    numDays = 28;
    dailyPct = Array.from({ length: numDays }, () => +(16 + rng() * 14).toFixed(2));
    validPct = dailyPct.slice();
    remPctiles = deriveRemPercentilesFromP50(21);
    demographicPct = remPctiles.p50;
  }

  let yMin = Math.min(...validPct, demographicPct);
  let yMax = Math.max(...validPct, demographicPct);
  const pad = Math.max(1.5, (yMax - yMin) * 0.15);
  yMin = Math.max(0, yMin - pad);
  yMax = unitLabel === '%' ? Math.min(100, yMax + pad) : yMax + pad;
  if (yMax - yMin < 6) {
    const mid = (yMax + yMin) / 2;
    yMin = Math.max(0, mid - 3);
    yMax = unitLabel === '%' ? Math.min(100, mid + 3) : mid + 3;
  }
  if (yMax - yMin < 1e-6) {
    yMin = Math.max(0, yMin - 2);
    yMax = yMax + 2;
  }

  let step = niceStepPercent(yMax - yMin, 5);
  let yTickStart = Math.floor(yMin / step) * step;
  let yTickEnd = Math.ceil(yMax / step) * step;
  while (step > 0 && (yTickEnd - yTickStart) / step > 9) {
    step *= 2;
    yTickStart = Math.floor(yMin / step) * step;
    yTickEnd = Math.ceil(yMax / step) * step;
  }

  const yTicks = [];
  for (let v = yTickEnd; v >= yTickStart - step * 0.25; v -= step) {
    yTicks.push(+v.toFixed(6));
    if (yTicks.length > 14) break;
  }

  const formatYTickLabel = v => {
    if (unitLabel === 's') {
      return nfcFormatSecondsAxis(v);
    }
    const fmt = unitLabel === '%' || unitLabel === 'ms' || unitLabel === 'bpm'
      ? String(Math.round(v))
      : (+v).toFixed(1);
    return `${fmt}${unitLabel}`;
  };
  const yTickLabels = yTicks.map(formatYTickLabel);

  const gutter = nfcYAxisGutter(yTickLabels);
  const rect = nfcPlotRect(gutter.plotLeft);
  const { plotLeft, plotRight, plotTop, plotBottom, plotW, plotH } = rect;
  void plotTop;
  const xDayLabelY = plotBottom + 14;

  /**
   * Cohort Y-scale: map the nice tick span [yTickStart, yTickEnd] onto the fixed plot
   * band [plotTop, plotBottom]. This keeps the top tick always at plotTop and the
   * bottom tick at plotBottom regardless of how many ticks fall between them.
   */
  const yTickSpan = Math.max(1e-9, yTickEnd - yTickStart);
  const scaleY = pct => {
    const p = Math.max(yTickStart, Math.min(yTickEnd, pct));
    return plotBottom - ((p - yTickStart) / yTickSpan) * plotH;
  };
  const demoY = scaleY(demographicPct);

  const slotW = plotW / numDays;
  const barW = Math.max(2, slotW * 0.7);
  const { p30: rp30, p60: rp60, p90: rp90 } = remPctiles;
  const bars = dailyPct
    .map((pct, i) => {
      if (pct == null || !Number.isFinite(pct)) return '';
      const x = plotLeft + i * slotW + (slotW - barW) / 2;
      const yTop = scaleY(pct);
      const h = Math.max(0, plotBottom - yTop);
      if (h < 0.5) return '';
      const fill = remFillForCohortPct(pct, rp30, rp60, rp90, lowerIsBetter);
      const band = remBandDesc(pct, rp30, rp60, rp90, lowerIsBetter);
      return `<rect x="${x.toFixed(2)}" y="${yTop.toFixed(2)}" width="${barW.toFixed(2)}" height="${h.toFixed(2)}" rx="1.2" fill="${fill}"><title>Day ${i + 1} · ${
        unitLabel === 's' ? nfcFormatSecondsAxis(pct) : `${(+pct).toFixed(1)}${unitLabel}`
      } ${metricLabel} · ${band}</title></rect>`;
    })
    .join('');

  const yLabels = yTicks
    .map((v, idx) => {
      const y = scaleY(v);
      return `<text class="${NFC_CLS_TICK}" x="${gutter.yLabelX.toFixed(2)}" y="${(y + 3).toFixed(2)}" text-anchor="end">${yTickLabels[idx]}</text>`;
    })
    .join('');

  const tickStep = Math.max(1, Math.ceil(numDays / 10));
  const xDayIndices = [];
  for (let d = 1; d <= numDays; d += tickStep) xDayIndices.push(d);
  if (xDayIndices[xDayIndices.length - 1] !== numDays) xDayIndices.push(numDays);

  const xLabels = xDayIndices
    .map(d => {
      const i = d - 1;
      const cx = plotLeft + (i + 0.5) * slotW;
      return `<text class="${NFC_CLS_TICK}" x="${cx.toFixed(2)}" y="${xDayLabelY}" text-anchor="middle">${String(d).padStart(2, '0')}</text>`;
    })
    .join('');

  const avgLine = `<line x1="${plotLeft}" y1="${demoY.toFixed(2)}" x2="${plotRight}" y2="${demoY.toFixed(2)}" stroke="#9ca3af" stroke-width="0.5" stroke-dasharray="4 3" stroke-linecap="round" opacity="0.95" vector-effect="non-scaling-stroke"/>`;

  const cohortRefText =
    unitLabel === 's' ? nfcFormatSecondsAxis(demographicPct) : `${Math.round(demographicPct)}${unitLabel}`;
  const cohortThresh = n => (unitLabel === 's' ? nfcFormatSecondsAxis(n) : `${n}${unitLabel}`);
  svg.setAttribute(
    'aria-label',
    `Daily ${metricLabel} over ${numDays} days; bars colored by cohort percentile band (red 0–30, yellow 30–60, green 60–90, blue 90+). Reference near ${cohortRefText}`,
  );

  svg.innerHTML = `
    <title>Daily ${metricLabel} versus ${baselineLabel}</title>
    <desc>Bars: nightly ${metricLabel}. Fill by approximate cohort percentile for age ${metaAge} (${metaGender}): red below ${cohortThresh(rp30)}, yellow ${cohortThresh(rp30)}–${cohortThresh(rp60)}, green ${cohortThresh(rp60)}–${cohortThresh(rp90)}, blue at or above ${cohortThresh(rp90)}. Dashed line: ${baselineLabel} near ${unitLabel === 's' ? nfcFormatSecondsAxis(demographicPct) : `${demographicPct.toFixed(1)}${unitLabel}`}.</desc>
    ${yLabels}
    ${bars}
    ${avgLine}
    ${xLabels}
  `;
  svg.setAttribute('viewBox', `0 0 ${NFC_VB_W} ${NFC_VB_H}`);
}

function nutricodeDrawFocusDeepInt(svg, data, slotSuffix) {
  const BLUE = '#0071e3';
  const YELLOW = '#ffd60a';
  const PT_FILTER_ID = 'fc2DualPtSh' + slotSuffix;
  const GRID = 'rgba(0, 0, 0, 0.08)';

  const seed = s => {
    let x = s;
    return () => {
      x = Math.sin(x) * 10000;
      return x - Math.floor(x);
    };
  };

  const _fc2 = data;
  const metricLabel = String(_fc2.metricLabel || 'Deep Sleep');
  const leftUnit = String(_fc2.leftUnit || _fc2.yUnit || '%');
  let metricRaw = Array.isArray(_fc2.metricDaily)
    ? _fc2.metricDaily.map((v) => (v == null ? null : +v))
    : Array.isArray(_fc2.deepDaily)
      ? _fc2.deepDaily.map((v) => (v == null ? null : +v))
      : [];
  let intensityRaw = Array.isArray(_fc2.intensityDaily)
    ? _fc2.intensityDaily.map((v) => (v == null ? null : +v))
    : [];

  /** Calendar days 7–30 → 24 points. If `metricDaily` / `deepDaily` has length 24, values are already 7-day averages (do not smooth again). */
  const PLOT_DAY_FIRST = 7;
const PLOT_DAY_LAST = 30;
  const i0 = PLOT_DAY_FIRST - 1;
  const i1 = PLOT_DAY_LAST - 1;
  const xIndexSpan = i1 - i0;
  const N_7D_WINDOW = i1 - i0 + 1;
  const metricPrepacked7d = metricRaw.length === N_7D_WINDOW;
  const intenPrepacked7d = intensityRaw.length === N_7D_WINDOW;

  if (!metricPrepacked7d) {
    while (metricRaw.length < 30) metricRaw.push(null);
    metricRaw = metricRaw.slice(0, 30);
  }
  if (!intenPrepacked7d) {
    while (intensityRaw.length < 30) intensityRaw.push(null);
    intensityRaw = intensityRaw.slice(0, 30);
  }

  const rng = seed(29);
  const hasDeep = metricRaw.some(v => v != null && Number.isFinite(v));
  const hasInt = intensityRaw.some(v => v != null && Number.isFinite(v));
  if (!hasDeep || !hasInt) {
    if (!hasDeep) {
      if (metricPrepacked7d) {
        for (let j = 0; j < N_7D_WINDOW; j += 1) {
          metricRaw[j] = +(15 + Math.sin((j + i0) / 3.5) * 1.4 + (rng() - 0.5) * 0.9).toFixed(2);
        }
      } else {
        for (let i = 0; i < 30; i += 1) {
          metricRaw[i] = +(15 + Math.sin(i / 3.5) * 1.4 + (rng() - 0.5) * 0.9).toFixed(2);
        }
      }
    }
    if (!hasInt) {
      if (intenPrepacked7d) {
        for (let j = 0; j < N_7D_WINDOW; j += 1) {
          intensityRaw[j] = +(1.32 - (j + i0) * 0.005 + Math.sin((j + i0) / 2.8) * 0.1 + (rng() - 0.5) * 0.035).toFixed(2);
        }
      } else {
        for (let i = 0; i < 30; i += 1) {
          intensityRaw[i] = +(1.32 - i * 0.005 + Math.sin(i / 2.8) * 0.1 + (rng() - 0.5) * 0.035).toFixed(2);
        }
      }
    }
  }

  const nfcCoerceMetric = v => (v != null && Number.isFinite(+v) ? +v : 0);

  const validD = [];
  const validI = [];
  for (let j = 0; j < N_7D_WINDOW; j += 1) {
    const dayIx = i0 + j;
    const vd = metricPrepacked7d ? metricRaw[j] : metricRaw[dayIx];
    const vi = intenPrepacked7d ? intensityRaw[j] : intensityRaw[dayIx];
    validD.push(nfcCoerceMetric(vd));
    validI.push(nfcCoerceMetric(vi));
  }

  const trimmedRange = arr => {
    if (arr.length < 4) return [Math.min(...arr), Math.max(...arr)];
    const sorted = [...arr].sort((a, b) => a - b);
    const cut = Math.max(1, Math.floor(sorted.length * 0.05));
    return [sorted[cut], sorted[sorted.length - 1 - cut]];
  };
  const [rawMinD, rawMaxD] = trimmedRange(validD);
  const [rawMinI, rawMaxI] = trimmedRange(validI);

  let minD = rawMinD;
  let maxD = rawMaxD;
  let minI = rawMinI;
  let maxI = rawMaxI;

  const padDy = Math.max(0.25, (maxD - minD) * 0.08);
  const padIy = Math.max(0.02, (maxI - minI) * 0.08);
  minD -= padDy;
  maxD += padDy;
  minI -= padIy;
  maxI += padIy;
  if (maxD - minD < 1e-6) { minD -= 1; maxD += 1; }
  if (maxI - minI < 1e-6) { minI -= 0.1; maxI += 0.1; }

  const nLeftTicks = 5;
  const nRightTicks = 5;
  const leftTicks = Array.from({ length: nLeftTicks }, (_, t) => maxD - (t / (nLeftTicks - 1)) * (maxD - minD));
  const rightTicks = Array.from({ length: nRightTicks }, (_, t) => maxI - (t / (nRightTicks - 1)) * (maxI - minI));

  const fmtLeft = v => {
    if (leftUnit === 's') {
      return nfcFormatSecondsAxis(v);
    }
    return leftUnit === '%' || leftUnit === 'ms' || leftUnit === 'bpm'
      ? `${Math.round(v)}${leftUnit}`
      : `${(+v).toFixed(1)}${leftUnit}`;
  };
  const fmtRight = v => (+v).toFixed(2);

  const leftLabelStrings = leftTicks.map(fmtLeft);
  const rightLabelStrings = rightTicks.map(fmtRight);

  /* Left gutter from left-axis labels; right gutter from right-axis labels. */
  const leftGutter = nfcYAxisGutter(leftLabelStrings);
  const rightLabelMaxW = rightLabelStrings.reduce((m, s) => Math.max(m, nfcTextWidth(s)), 0);
  const plotLeft = leftGutter.plotLeft;
  const plotRight = NFC_VB_W - NFC_PAD_RIGHT - rightLabelMaxW - NFC_GUTTER_GAP;
  const plotTop = NFC_PAD_TOP;
  const plotBottom = NFC_VB_H - NFC_PAD_BOTTOM_AXIS;
  const plotW = plotRight - plotLeft;
  const plotH = plotBottom - plotTop;
  const leftLabelX = leftGutter.yLabelX;
  const rightLabelX = plotRight + NFC_GUTTER_GAP;
  const xDayLabelY = plotBottom + 14;

  const scaleX = i => plotLeft + ((i - i0) / xIndexSpan) * plotW;
  const scaleYd = v => plotBottom - ((v - minD) / (maxD - minD)) * plotH;
  const scaleYi = v => plotBottom - ((v - minI) / (maxI - minI)) * plotH;
  const clampY = y => Math.min(plotBottom, Math.max(plotTop, y));

  const ptFilterDefs = `<defs><filter id="${PT_FILTER_ID}" x="-100%" y="-100%" width="300%" height="300%"><feDropShadow dx="0" dy="1" stdDeviation="1" flood-color="#000000" flood-opacity="0.12"/><feDropShadow dx="0" dy="0.5" stdDeviation="0.5" flood-color="#000000" flood-opacity="0.08"/></filter></defs>`;

  const ptsDeep = [];
  const ptsInt = [];
  for (let j = 0; j < N_7D_WINDOW; j += 1) {
    const dayIx = i0 + j;
    const vd = nfcCoerceMetric(metricPrepacked7d ? metricRaw[j] : metricRaw[dayIx]);
    const vi = nfcCoerceMetric(intenPrepacked7d ? intensityRaw[j] : intensityRaw[dayIx]);
    ptsDeep.push({ i: dayIx, x: scaleX(dayIx), y: clampY(scaleYd(vd)), v: vd });
    ptsInt.push({ i: dayIx, x: scaleX(dayIx), y: clampY(scaleYi(vi)), v: vi });
  }
  const polyPts = arr => arr.map(p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');
  const lineDeep = ptsDeep.length >= 2
    ? `<polyline class="fc2-connect fc2-connect--deep" points="${polyPts(ptsDeep)}" fill="none" stroke="${BLUE}" stroke-linecap="round" stroke-linejoin="round" stroke-width="1" shape-rendering="geometricPrecision" opacity="0.85"/>`
    : '';
  const lineInt = ptsInt.length >= 2
    ? `<polyline class="fc2-connect fc2-connect--int" points="${polyPts(ptsInt)}" fill="none" stroke="${YELLOW}" stroke-linecap="round" stroke-linejoin="round" stroke-width="1" shape-rendering="geometricPrecision" opacity="0.9"/>`
    : '';
  const deepPointTitle = p =>
    leftUnit === 's'
      ? `${metricLabel} ${nfcFormatSecondsAxis(p.v)}`
      : `${metricLabel} ${(+p.v).toFixed(leftUnit === '%' ? 1 : 2)}${leftUnit}`;
  const circlesDeep = ptsDeep
    .map(p => `<circle class="fc2-marker-deep" cx="${p.x.toFixed(2)}" cy="${p.y.toFixed(2)}" r="3" fill="${BLUE}" stroke="#ffffff" stroke-width="2" filter="url(#${PT_FILTER_ID})"><title>Day ${p.i + 1} · ${deepPointTitle(p)}</title></circle>`)
    .join('');
  const circlesInt = ptsInt
    .map(p => `<circle class="fc2-marker-int" cx="${p.x.toFixed(2)}" cy="${p.y.toFixed(2)}" r="3" fill="${YELLOW}" stroke="#ffffff" stroke-width="2" filter="url(#${PT_FILTER_ID})"><title>Day ${p.i + 1} · Training intensity ${(+p.v).toFixed(2)}</title></circle>`)
    .join('');

  const hLines = leftTicks
    .map(v => {
      const y = Math.round(scaleYd(v) * 2) / 2;
      return `<line class="fc2-hgrid" x1="${plotLeft}" y1="${y}" x2="${plotRight.toFixed(2)}" y2="${y}" stroke="${GRID}"/>`;
    })
    .join('');

  const leftLbl = leftTicks
    .map((v, idx) => {
      const y = Math.round(scaleYd(v) * 2) / 2;
      return `<text class="${NFC_CLS_TICK} nfc-axis-tick--deep" x="${leftLabelX.toFixed(2)}" y="${(y + 3).toFixed(2)}" text-anchor="end">${leftLabelStrings[idx]}</text>`;
    })
    .join('');

  const rightLbl = rightTicks
    .map((v, idx) => {
      const y = Math.round(scaleYi(v) * 2) / 2;
      return `<text class="${NFC_CLS_TICK} nfc-axis-tick--load" x="${rightLabelX.toFixed(2)}" y="${(y + 3).toFixed(2)}" text-anchor="start">${rightLabelStrings[idx]}</text>`;
    })
    .join('');

  const xTickDays = [7, 11, 15, 19, 23, 27, 30];
  const xLbl = xTickDays
    .map(day => {
      const x = scaleX(day - 1);
      const anchor = day === PLOT_DAY_FIRST ? 'start' : day === PLOT_DAY_LAST ? 'end' : 'middle';
      return `<text class="${NFC_CLS_TICK}" x="${x.toFixed(2)}" y="${xDayLabelY}" text-anchor="${anchor}">${String(day).padStart(2, '0')}</text>`;
    })
    .join('');

  const parts = [
    `<title>Daily ${metricLabel} and training load by day.</title>`,
    `<desc>Blue: ${metricLabel} (7-day average values from chart data, not recomputed), yellow: training intensity. Calendar days ${PLOT_DAY_FIRST}–${PLOT_DAY_LAST}; missing values plotted as 0.</desc>`,
    ptFilterDefs,
    hLines,
    lineDeep,
    lineInt,
    circlesDeep,
    circlesInt,
    leftLbl,
    rightLbl,
    xLbl,
  ];

  svg.innerHTML = parts.join('');
  svg.setAttribute('viewBox', `0 0 ${NFC_VB_W} ${NFC_VB_H}`);
  svg.setAttribute(
    'aria-label',
    `Daily ${metricLabel} (blue) and training intensity (yellow), 7-day averages for days ${PLOT_DAY_FIRST} through ${PLOT_DAY_LAST}`,
  );
}

function nutricodeDrawFocusHrv(svg, data, slotSuffix) {
  const d = data;
  const metricLabel = String(d.metricLabel || 'HRV');
  const unitLabel = String(d.unitLabel || d.yUnit || 'ms');
  const titleLabel = String(d.titleLabel || `Nightly ${metricLabel}`);
  const numDays = Math.max(1, Math.min(31, +d.numDays || 30));
  const seriesByDay = Array.isArray(d.seriesByDay)
    ? d.seriesByDay
    : Array.isArray(d.hrvByDay) ? d.hrvByDay : [];
  let targetMin = +d.targetMin;
  let targetMax = +d.targetMax;
  if (!Number.isFinite(targetMin)) targetMin = 100;
  if (!Number.isFinite(targetMax)) targetMax = 110;
  if (targetMin > targetMax) { const t = targetMin; targetMin = targetMax; targetMax = t; }

  const fmtTick = v => {
    const n = +v;
    if (!Number.isFinite(n)) return '—';
    if (unitLabel === 's') {
      return nfcFormatSecondsAxis(n);
    }
    if (unitLabel === 'h') return `${n.toFixed(1)} ${unitLabel}`.trim();
    if (unitLabel === '/h') return `${n.toFixed(2)} ${unitLabel}`.trim();
    return `${Math.round(n)} ${unitLabel}`.trim();
  };
  const fmtPointTitle = fmtTick;

  const minSpan =
    unitLabel === '/h' ? 0.35
    : unitLabel === 'h' ? 1.2
    : unitLabel === '%' ? 8
    : unitLabel === 's' ? 300
    : 12;

  const vals = seriesByDay.slice(0, numDays).filter(v => v != null && Number.isFinite(+v)).map(v => +v);
  let yMin = Math.min(targetMin, ...(vals.length ? vals : [targetMin]));
  let yMax = Math.max(targetMax, ...(vals.length ? vals : [targetMax]));
  const pad = Math.max((yMax - yMin) * 0.12, unitLabel === '/h' ? 0.02 : unitLabel === 'h' ? 0.15 : 1);
  yMin = Math.max(0, yMin - pad);
  yMax = yMax + pad;
  if (yMax - yMin < minSpan) {
    const mid = (yMax + yMin) / 2;
    yMin = mid - minSpan / 2;
    yMax = mid + minSpan / 2;
  }

  const gridCount = 6;
  const gridMs = [];
  for (let g = 0; g < gridCount; g++) {
    gridMs.push(yMax - (g / (gridCount - 1)) * (yMax - yMin));
  }
  const gridLabels = gridMs.map(fmtTick);

  const gutter = nfcYAxisGutter(gridLabels);
  const rect = nfcPlotRect(gutter.plotLeft);
  const { plotLeft, plotRight, plotTop, plotBottom, plotW, plotH } = rect;
  const xDayLabelY = plotBottom + 14;

  const ySpan = Math.max(1e-6, yMax - yMin);
  const scaleY = ms => plotBottom - ((ms - yMin) / ySpan) * plotH;
  void plotTop;

  const bandTop = Math.min(scaleY(targetMin), scaleY(targetMax));
  const bandH = Math.abs(scaleY(targetMax) - scaleY(targetMin));

  const filterId = 'fc3HrvPtSh' + slotSuffix;
  let out = `<defs><filter id="${filterId}" x="-100%" y="-100%" width="300%" height="300%"><feDropShadow dx="0" dy="1" stdDeviation="1" flood-color="#000000" flood-opacity="0.12"/><feDropShadow dx="0" dy="0.5" stdDeviation="0.5" flood-color="#000000" flood-opacity="0.08"/></filter></defs>`;
  out += `<line x1="${plotLeft}" y1="${plotBottom}" x2="${plotRight}" y2="${plotBottom}" stroke="#e8e8ed" stroke-width="1"/>`;
  out += `<rect x="${plotLeft}" y="${bandTop.toFixed(2)}" width="${plotW}" height="${Math.max(bandH, 1).toFixed(2)}" rx="1.2" fill="rgba(59, 130, 246, 0.18)"/>`;
  gridMs.forEach(ms => {
    const yy = scaleY(ms);
    out += `<line x1="${plotLeft}" y1="${yy.toFixed(2)}" x2="${plotRight}" y2="${yy.toFixed(2)}" stroke="#f0f0f2" stroke-width="1"/>`;
  });
  gridMs.forEach((ms, idx) => {
    const yy = scaleY(ms);
    out += `<text class="${NFC_CLS_TICK}" x="${gutter.yLabelX.toFixed(2)}" y="${yy.toFixed(2)}" text-anchor="end" dominant-baseline="middle">${gridLabels[idx]}</text>`;
  });

  const slotW = plotW / numDays;
  for (let i = 0; i < numDays; i++) {
    const v = seriesByDay[i];
    if (v == null || !Number.isFinite(+v)) continue;
    const yv = +v;
    const cx = plotLeft + i * slotW + slotW / 2;
    const cy = scaleY(yv);
    const inBand = yv >= targetMin && yv <= targetMax;
    const fill = inBand ? '#0071e3' : '#aeaeb2';
    out += `<circle cx="${cx.toFixed(2)}" cy="${cy.toFixed(2)}" r="3" fill="${fill}" stroke="#ffffff" stroke-width="2" filter="url(#${filterId})"><title>Day ${i + 1} · ${metricLabel} ${fmtPointTitle(yv)}</title></circle>`;
  }

  const tickStep = Math.max(1, Math.ceil(numDays / 10));
  const xDayIndices = [];
  for (let day = 1; day <= numDays; day += tickStep) xDayIndices.push(day);
  if (xDayIndices[xDayIndices.length - 1] !== numDays) xDayIndices.push(numDays);
  xDayIndices.forEach(day => {
    const i = day - 1;
    const cx = plotLeft + (i + 0.5) * slotW;
    out += `<text class="${NFC_CLS_TICK}" x="${cx.toFixed(2)}" y="${xDayLabelY}" text-anchor="middle" dominant-baseline="middle">${String(day).padStart(2, '0')}</text>`;
  });

  const ariaTarget =
    unitLabel === 's'
      ? `${nfcFormatSecondsAxis(targetMin)}–${nfcFormatSecondsAxis(targetMax)}`
      : unitLabel === '/h' || unitLabel === 'h'
        ? `${Number(targetMin).toFixed(unitLabel === 'h' ? 1 : 2)}–${Number(targetMax).toFixed(unitLabel === 'h' ? 1 : 2)} ${unitLabel}`
        : `${Math.round(targetMin)}–${Math.round(targetMax)} ${unitLabel}`;
  svg.setAttribute('aria-label', `Nightly ${metricLabel} over ${numDays} nights; target ${ariaTarget}`);
  svg.innerHTML = `<title>${titleLabel}</title><desc>Shaded band: target range for ${metricLabel}. Blue dots are within range.</desc>${out}`;
  svg.setAttribute('viewBox', `0 0 ${NFC_VB_W} ${NFC_VB_H}`);
}

function nutricodeDrawFocusScatter(svg, data, slotSuffix) {
  const slotSuf = String(slotSuffix);
  const yAxisLabelText = String(data.yAxisLabel || 'NEXT-DAY RHR');
  const xAxisLabelText = String(data.xAxisLabel || 'Training Intensity');
  const yUnit = String(data.yUnit || 'bpm');
  /** When set to "s" (e.g. sleep duration on X or Y), axis and tooltips use `nfcFormatSecondsAxis` instead of raw seconds. */
  const xUnit = String(data.xUnit != null ? data.xUnit : '');
  const pointYLabel = String(data.pointYLabel || 'Next-day RHR');
  const GRID = '#f0f0f2';
  const BLUE = '#0071e3';

  const seed = s => {
    let x = s;
    return () => { x = Math.sin(x) * 10000; return x - Math.floor(x); };
  };
  const niceStep = (range, ticks) => {
    const rough = Math.max(range, 0.0001) / Math.max(1, ticks - 1);
    const power = 10 ** Math.floor(Math.log10(rough));
    const scaled = rough / power;
    let factor = 1;
    if (scaled <= 1) factor = 1;
    else if (scaled <= 2) factor = 2;
    else if (scaled <= 2.5) factor = 2.5;
    else if (scaled <= 5) factor = 5;
    else factor = 10;
    return factor * power;
  };
  const buildTicks = (min, max, count) => {
    const step = niceStep(max - min, count);
    const start = Math.floor(min / step) * step;
    const end = Math.ceil(max / step) * step;
    const ticks = [];
    for (let value = start; value <= end + step * 0.5; value += step) {
      ticks.push(+value.toFixed(4));
    }
    return ticks;
  };

  const formatXTick = value => {
    if (xUnit === 's') {
      return nfcFormatSecondsAxis(value);
    }
    return (+value).toFixed(1);
  };
  const formatYTick = value => {
    const v = +value;
    if (!Number.isFinite(v)) return '—';
    if (yUnit === 's') {
      return nfcFormatSecondsAxis(v);
    }
    if (yUnit === 'h') return v.toFixed(1);
    if (yUnit === '/h') return v.toFixed(2);
    return `${Math.round(v)}`;
  };
  const formatYPointTitle = y => {
    const v = +y;
    if (!Number.isFinite(v)) return '—';
    if (yUnit === 's') {
      return nfcFormatSecondsAxis(v);
    }
    if (yUnit === 'h') return `${v.toFixed(1)} ${yUnit}`.trim();
    if (yUnit === '/h') return `${v.toFixed(2)} ${yUnit}`.trim();
    return `${Math.round(v)} ${yUnit}`.trim();
  };
  const formatXPointTitle = x => {
    const v = +x;
    if (!Number.isFinite(v)) return '—';
    if (xUnit === 's') {
      return nfcFormatSecondsAxis(v);
    }
    return v.toFixed(2);
  };

  const _fc4 = data;
  let pts = Array.isArray(_fc4.points)
    ? _fc4.points
        .filter(p => p && Number.isFinite(+p.x) && Number.isFinite(+p.y))
        .map(p => ({ x: +Number(p.x).toFixed(4), y: +Number(p.y).toFixed(2) }))
    : [];

  if (pts.length < 2) {
    const rng = seed(7);
    pts = Array.from({ length: 14 }, () => ({
      x: +(rng() * 1.3 + 1.05).toFixed(2),
      y: Math.round(48 + rng() * 8),
    }));
  }

  const xs = pts.map(p => p.x);
  const ys = pts.map(p => p.y);
  const n = pts.length;
  const meanX = xs.reduce((sum, value) => sum + value, 0) / n;
  const meanY = ys.reduce((sum, value) => sum + value, 0) / n;

  let num = 0;
  let dx2 = 0;
  pts.forEach(point => {
    num += (point.x - meanX) * (point.y - meanY);
    dx2 += (point.x - meanX) ** 2;
  });

  const slope = dx2 > 1e-12 ? num / dx2 : 0;
  const intercept = meanY - slope * meanX;
  const predict = x => slope * x + intercept;

  const rawXMin = Math.min(...xs);
  const rawXMax = Math.max(...xs);
  const rawYMin = Math.min(...ys, predict(rawXMin), predict(rawXMax));
  const rawYMax = Math.max(...ys, predict(rawXMin), predict(rawXMax));

  const xPad = Math.max(
    xUnit === 's' ? 120 : 0.08,
    (rawXMax - rawXMin || (xUnit === 's' ? 300 : 0.6)) * 0.18,
  );
  const yPadBase = yUnit === '/h' ? 0.02 : yUnit === 'h' ? 0.25 : yUnit === 's' ? 300 : 1.5;
  const yPad = Math.max(
    yPadBase,
    (rawYMax - rawYMin ||
      (yUnit === '/h' ? 0.1 : yUnit === 'h' ? 0.5 : yUnit === 's' ? 600 : 5)) * 0.22,
  );

  const xTicks = buildTicks(rawXMin - xPad, rawXMax + xPad, 6);
  const yTicks = buildTicks(rawYMin - yPad, rawYMax + yPad, 5);

  const domainXMin = xTicks[0];
  const domainXMax = xTicks[xTicks.length - 1];
  const domainYMin = yTicks[0];
  const domainYMax = yTicks[yTicks.length - 1];

  const yTickLabels = yTicks.map(formatYTick);
  const gutter = nfcYAxisGutter(yTickLabels, NFC_TICK_FONT_PX, NFC_AXIS_TITLE_GUTTER);
  const rect = nfcPlotRect(gutter.plotLeft, NFC_PAD_BOTTOM_WITH_XTITLE);
  const plot = {
    left: rect.plotLeft,
    right: rect.plotRight,
    top: rect.plotTop,
    bottom: rect.plotBottom,
  };
  const plotMidX = (plot.left + plot.right) / 2;
  const plotMidY = (plot.top + plot.bottom) / 2;
  const plotWidth = plot.right - plot.left;
  const plotHeight = plot.bottom - plot.top;
  /*
   * Rotated Y-title hugs the left edge: its visual horizontal band is ~1 em
   * wide after rotation, so placing the center at NFC_PAD_LEFT_MIN + em/2
   * puts the glyph band's left edge exactly at NFC_PAD_LEFT_MIN (rather
   * than floating in the middle of the gutter, which made it look far from
   * the panel border).
   */
  const yAxisTitleX = NFC_PAD_LEFT_MIN + NFC_TICK_FONT_PX / 2;
  const xTickY = plot.bottom + NFC_XTICK_BELOW_PLOT;
  const xAxisTitleY = plot.bottom + NFC_XTITLE_BELOW_PLOT;

  const scaleX = value => plot.left + ((value - domainXMin) / (domainXMax - domainXMin)) * plotWidth;
  const scaleY = value => plot.bottom - ((value - domainYMin) / (domainYMax - domainYMin)) * plotHeight;

  const horizontalGrid = yTicks.slice(1).map(value => {
    const y = scaleY(value).toFixed(2);
    return `<line x1="${plot.left}" y1="${y}" x2="${plot.right}" y2="${y}" stroke="${GRID}" stroke-width="1"></line>`;
  }).join('');

  const xLabels = xTicks.map((value, index) => {
    const x = scaleX(value).toFixed(2);
    const isLast = index === xTicks.length - 1;
    const anchor = index === 0 ? 'start' : isLast ? 'end' : 'middle';
    return `<text class="${NFC_CLS_TICK}" x="${x}" y="${xTickY}" text-anchor="${anchor}">${formatXTick(value)}</text>`;
  }).join('');

  const yLabels = yTicks.map((value, idx) => {
    const y = scaleY(value).toFixed(2);
    return `<text class="${NFC_CLS_TICK}" x="${gutter.yLabelX.toFixed(2)}" y="${(+y + 3).toFixed(2)}" text-anchor="end">${yTickLabels[idx]}</text>`;
  }).join('');

  /* Rotated Y-title "runs" along the plot's vertical span; X-title along its
   * horizontal span. Cap each to their plot-axis length so that on small
   * phones (where the user-unit font-size grows) the glyphs compress rather
   * than overflow the viewBox. */
  const yTitleFit = nfcAxisTitleAttrs(yAxisLabelText, plotHeight);
  const xTitleFit = nfcAxisTitleAttrs(xAxisLabelText, plotWidth);
  const yAxisTitle = `<text class="${NFC_CLS_AXIS_TITLE}" transform="rotate(-90, ${yAxisTitleX.toFixed(2)}, ${plotMidY.toFixed(2)})" x="${yAxisTitleX.toFixed(2)}" y="${plotMidY.toFixed(2)}" text-anchor="middle" dominant-baseline="middle"${yTitleFit}>${yAxisLabelText}</text>`;
  const xAxisTitle = `<text class="${NFC_CLS_AXIS_TITLE}" x="${plotMidX.toFixed(2)}" y="${xAxisTitleY}" text-anchor="middle"${xTitleFit}>${xAxisLabelText}</text>`;

  const regressionLine = `<line x1="${scaleX(domainXMin).toFixed(2)}" y1="${scaleY(predict(domainXMin)).toFixed(2)}" x2="${scaleX(domainXMax).toFixed(2)}" y2="${scaleY(predict(domainXMax)).toFixed(2)}" stroke="${BLUE}" stroke-width="1.5" stroke-linecap="round"></line>`;

  const pointShadowFilter = `<filter id="focusScatterPtSh${slotSuf}" x="-100%" y="-100%" width="300%" height="300%"><feDropShadow dx="0" dy="1" stdDeviation="1" flood-color="#000000" flood-opacity="0.12"/><feDropShadow dx="0" dy="0.5" stdDeviation="0.5" flood-color="#000000" flood-opacity="0.08"/></filter>`;
  const circles = pts
    .map(
      point => `<circle cx="${scaleX(point.x).toFixed(2)}" cy="${scaleY(point.y).toFixed(2)}" r="3" fill="#aeaeb2" stroke="#ffffff" stroke-width="2" filter="url(#focusScatterPtSh${slotSuf})"><title>${xAxisLabelText} ${formatXPointTitle(
        point.x,
      )} · ${pointYLabel} ${formatYPointTitle(point.y)}</title></circle>`,
    )
    .join('');

  const plotClip = `<clipPath id="focusScatterPlotClip${slotSuf}"><rect x="${plot.left}" y="${plot.top}" width="${plotWidth}" height="${plotHeight}"/></clipPath>`;

  svg.innerHTML = `
    <defs>${pointShadowFilter}${plotClip}</defs>
    <title>${pointYLabel} versus same-day training intensity.</title>
    <desc>Scatter of training intensity versus ${pointYLabel}. Y-axis: ${pointYLabel} (${yUnit}). X-axis: training intensity.</desc>
    <g clip-path="url(#focusScatterPlotClip${slotSuf})">
      ${horizontalGrid}
      ${regressionLine}
      ${circles}
    </g>
    ${yAxisTitle}
    ${yLabels}
    ${xLabels}
    ${xAxisTitle}
  `;

  svg.setAttribute('viewBox', `0 0 ${NFC_VB_W} ${NFC_VB_H}`);
  svg.setAttribute('class', 'nfc-focus-scatter-svg');
}

/* ==================================================================== */
/* Tier-5 static SVGs (healthspan / systems / energy / inflammation)     */
/* ==================================================================== */

/**
 * Shared plot rect for tier-5 charts. They all have a rotated Y title and a
 * bottom-centered X title, so we reserve NFC_AXIS_TITLE_GUTTER on the left
 * and NFC_PAD_BOTTOM_WITH_XTITLE at the bottom.
 */
function nutricodeExtChartPlotBox(yTickLabels) {
  const gutter = nfcYAxisGutter(yTickLabels, NFC_TICK_FONT_PX, NFC_AXIS_TITLE_GUTTER);
  const rect = nfcPlotRect(gutter.plotLeft, NFC_PAD_BOTTOM_WITH_XTITLE);
  return {
    XL: rect.plotLeft,
    XR: rect.plotRight,
    YT: rect.plotTop,
    YB: rect.plotBottom,
    W: rect.plotW,
    H: rect.plotH,
    yLabelX: gutter.yLabelX,
    /*
     * See nutricodeDrawFocusScatter: place the rotated title's glyph band
     * flush against NFC_PAD_LEFT_MIN instead of centering it in the gutter.
     */
    yAxisTitleX: NFC_PAD_LEFT_MIN + NFC_TICK_FONT_PX / 2,
    xTickY: rect.plotBottom + NFC_XTICK_BELOW_PLOT,
    xAxisTitleY: rect.plotBottom + NFC_XTITLE_BELOW_PLOT,
  };
}

function nutricodeExtPathFromFn(xOf, yOf, fn, steps = 56) {
  const pts = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const p = Math.max(0, Math.min(1, fn(t)));
    pts.push({ x: xOf(t), y: yOf(p) });
  }
  const head = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  if (pts.length === 1) return head;
  return `${head} L ${pts.slice(1).map(q => `${q.x.toFixed(1)} ${q.y.toFixed(1)}`).join(' L ')}`;
}

function nutricodeHealthspanLifespanVsHealthspanSvg(idSuffix, svgId) {
  const yTickDefs = [
    { p: 1, lab: '100%' },
    { p: 0.75, lab: '75%' },
    { p: 0.5, lab: '50%' },
    { p: 0.25, lab: '25%' },
  ];
  const box = nutricodeExtChartPlotBox(yTickDefs.map(t => t.lab));
  const { XL, XR, YT, YB, W, H, yLabelX, yAxisTitleX, xTickY, xAxisTitleY } = box;
  const xOf = t => XL + t * W;
  const yOf = p => YB - p * H;

  function fnNormal(t) {
    if (t <= 0.2) return 1;
    if (t >= 1) return 0.06;
    const u = (t - 0.2) / 0.8;
    return 1 - 0.94 * (u * u * (3 - 2 * u));
  }
  function fnProtocol(t) {
    if (t <= 0.62) return 1;
    if (t >= 1) return 0.08;
    const u = (t - 0.62) / 0.38;
    return 1 - 0.92 * Math.pow(u, 6.2);
  }
  function samplePath(fn, steps = 64) {
    const pts = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const p = Math.max(0, Math.min(1, fn(t)));
      pts.push({ x: xOf(t), y: yOf(p) });
    }
    const head = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
    if (pts.length === 1) return head;
    return `${head} L ${pts.slice(1).map(q => `${q.x.toFixed(1)} ${q.y.toFixed(1)}`).join(' L ')}`;
  }
  const dNormal = samplePath(fnNormal);
  const dProtocol = samplePath(fnProtocol);

  const yTickSvg = yTickDefs
    .map(({ p, lab }) => {
      const y = yOf(p);
      return `<line x1="${XL}" y1="${y.toFixed(1)}" x2="${XR}" y2="${y.toFixed(1)}" stroke="#f0f0f2" stroke-width="1" vector-effect="non-scaling-stroke"/><text class="${NFC_CLS_TICK}" x="${yLabelX.toFixed(2)}" y="${(y + 3).toFixed(1)}" text-anchor="end">${lab}</text>`;
    })
    .join('\n');

  const ages = [20, 40, 60, 80, 100];
  const xTickSvg = ages
    .map(age => {
      const t = (age - 20) / 80;
      const x = xOf(t);
      return `<text class="${NFC_CLS_TICK}" x="${x.toFixed(1)}" y="${xTickY}" text-anchor="middle">${age}</text>`;
    })
    .join('\n');

  const aria = nutricodeEscAttr('Lifespan versus healthspan: illustrative Normal trajectory and With Protocol trajectory.');
  const midX = ((XL + XR) / 2).toFixed(1);
  const midY = ((YT + YB) / 2).toFixed(2);
  return `<svg id="${svgId}" class="focus-healthspan-svg" viewBox="0 0 ${NFC_VB_W} ${NFC_VB_H}" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" role="img" aria-label="${aria}">
    <title>Longevity: healthspan over lifespan</title>
    <desc>Normal (gray) versus With Protocol (green).</desc>
    <rect x="${XL}" y="${YT}" width="${W.toFixed(1)}" height="${H.toFixed(1)}" rx="0" fill="none"/>
    ${yTickSvg}
    <line x1="${XL}" y1="${YT}" x2="${XL}" y2="${YB}" stroke="#e5e7eb" stroke-width="1" vector-effect="non-scaling-stroke"/>
    <line x1="${XL}" y1="${YB}" x2="${XR}" y2="${YB}" stroke="#e5e7eb" stroke-width="1" vector-effect="non-scaling-stroke"/>
    <text class="${NFC_CLS_AXIS_TITLE}" transform="rotate(-90, ${yAxisTitleX.toFixed(2)}, ${midY})" x="${yAxisTitleX.toFixed(2)}" y="${midY}" text-anchor="middle" dominant-baseline="middle"${nfcAxisTitleAttrs('Healthspan', H)}>Healthspan</text>
    <path d="${dNormal}" stroke="#6b7280" stroke-width="1.75" fill="none" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"/>
    <path d="${dProtocol}" stroke="#10b981" stroke-width="1.75" fill="none" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"/>
    ${xTickSvg}
    <text class="${NFC_CLS_AXIS_TITLE}" x="${midX}" y="${xAxisTitleY}" text-anchor="middle"${nfcAxisTitleAttrs('Lifespan', W)}>Lifespan</text>
  </svg>`;
}

function nutricodeExtensionSystemDeclineSvg(idSuffix, svgId) {
  const yTickDefs = [
    { p: 1, lab: '100%' },
    { p: 0.75, lab: '75%' },
    { p: 0.5, lab: '50%' },
    { p: 0.25, lab: '25%' },
  ];
  const box = nutricodeExtChartPlotBox(yTickDefs.map(t => t.lab));
  const { XL, XR, YT, YB, W, H, yLabelX, yAxisTitleX, xTickY, xAxisTitleY } = box;
  const xOf = t => XL + t * W;
  const yOf = p => YB - p * H;
  /**
   * Smooth decline from 1 at t=start toward floor at t=1.
   * `span` is the nominal duration of the transition; if start + span > 1,
   * the old code used (t-start)/span which never reached u=1 before t=1,
   * then `t >= 1` snapped to floor — a visible kink on the green curve.
   * Clamp the denominator so u=1 exactly at t=1 (curve meets floor smoothly).
   */
  const decline = (t, start, span, floor) => {
    if (t <= start) return 1;
    if (t >= 1) return floor;
    const denom = Math.max(1e-6, Math.min(span, 1 - start));
    const u = Math.min(1, Math.max(0, (t - start) / denom));
    const s = u * u * (3 - 2 * u);
    return 1 - (1 - floor) * s;
  };
  const series = [
    { c: '#ef4444', fn: t => decline(t, 0.08, 0.72, 0.08) },
    { c: '#3b82f6', fn: t => decline(t, 0.14, 0.78, 0.12) },
    { c: '#8b5cf6', fn: t => decline(t, 0.2, 0.82, 0.14) },
    { c: '#10b981', fn: t => decline(t, 0.26, 0.86, 0.18) },
  ];
  const paths = series.map(s => `<path d="${nutricodeExtPathFromFn(xOf, yOf, s.fn)}" stroke="${s.c}" stroke-width="1.6" fill="none" stroke-linecap="round" vector-effect="non-scaling-stroke"/>`);
  const yLabs = yTickDefs.map(({ p, lab }) => {
    const y = yOf(p);
    return `<line x1="${XL}" y1="${y.toFixed(1)}" x2="${XR}" y2="${y.toFixed(1)}" stroke="#f0f0f2" stroke-width="1"/><text class="${NFC_CLS_TICK}" x="${yLabelX.toFixed(2)}" y="${(y + 3).toFixed(1)}" text-anchor="end">${lab}</text>`;
  });
  const ages = [20, 40, 60, 80, 100];
  const xLabs = ages.map(a => {
    const t = (a - 20) / 80;
    return `<text class="${NFC_CLS_TICK}" x="${xOf(t).toFixed(1)}" y="${xTickY}" text-anchor="middle">${a}</text>`;
  });
  const aria = nutricodeEscAttr('System function vs lifespan: Gut health, Immunity, Metabolism, Musculoskeletal illustrative decline curves.');
  const midX = ((XL + XR) / 2).toFixed(1);
  const midY = ((YT + YB) / 2).toFixed(2);
  return `<svg id="${svgId}" class="focus-extension-svg" viewBox="0 0 ${NFC_VB_W} ${NFC_VB_H}" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" role="img" aria-label="${aria}">
    <rect x="${XL}" y="${YT}" width="${W.toFixed(1)}" height="${H.toFixed(1)}" fill="none"/>
    ${yLabs.join('')}
    <line x1="${XL}" y1="${YT}" x2="${XL}" y2="${YB}" stroke="#e5e7eb" stroke-width="1"/><line x1="${XL}" y1="${YB}" x2="${XR}" y2="${YB}" stroke="#e5e7eb" stroke-width="1"/>
    <text class="${NFC_CLS_AXIS_TITLE}" transform="rotate(-90, ${yAxisTitleX.toFixed(2)}, ${midY})" x="${yAxisTitleX.toFixed(2)}" y="${midY}" text-anchor="middle" dominant-baseline="middle"${nfcAxisTitleAttrs('System Function', H)}>System Function</text>
    ${paths.join('')}
    ${xLabs.join('')}
    <text class="${NFC_CLS_AXIS_TITLE}" x="${midX}" y="${xAxisTitleY}" text-anchor="middle"${nfcAxisTitleAttrs('Lifespan', W)}>Lifespan</text>
  </svg>`;
}

function nutricodeExtensionEnergyOverAgeSvg(idSuffix, svgId) {
  const yTickDefs = [
    { p: 1, lab: '100%' },
    { p: 0.75, lab: '75%' },
    { p: 0.5, lab: '50%' },
    { p: 0.25, lab: '25%' },
  ];
  const box = nutricodeExtChartPlotBox(yTickDefs.map(t => t.lab));
  const { XL, XR, YT, YB, W, H, yLabelX, yAxisTitleX, xTickY, xAxisTitleY } = box;
  const xOf = t => XL + t * W;
  const yOf = p => YB - p * H;
  const fnEnergy = t => {
    const u = Math.max(0, Math.min(1, t));
    return 1 - 0.7 * Math.pow(u, 1.55);
  };
  const d = nutricodeExtPathFromFn(xOf, yOf, fnEnergy, 64);
  const yLabs = yTickDefs.map(({ p, lab }) => {
    const y = yOf(p);
    return `<line x1="${XL}" y1="${y.toFixed(1)}" x2="${XR}" y2="${y.toFixed(1)}" stroke="#f0f0f2" stroke-width="1"/><text class="${NFC_CLS_TICK}" x="${yLabelX.toFixed(2)}" y="${(y + 3).toFixed(1)}" text-anchor="end">${lab}</text>`;
  });
  const ages = [20, 40, 60, 80, 100];
  const xLabs = ages.map(a => {
    const t = (a - 20) / 80;
    return `<text class="${NFC_CLS_TICK}" x="${xOf(t).toFixed(1)}" y="${xTickY}" text-anchor="middle">${a}</text>`;
  });
  const aria = nutricodeEscAttr('Energy levels across age; illustrative curve.');
  const midX = ((XL + XR) / 2).toFixed(1);
  const midY = ((YT + YB) / 2).toFixed(2);
  return `<svg id="${svgId}" class="focus-extension-svg" viewBox="0 0 ${NFC_VB_W} ${NFC_VB_H}" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" role="img" aria-label="${aria}">
    <rect x="${XL}" y="${YT}" width="${W.toFixed(1)}" height="${H.toFixed(1)}" fill="none"/>
    ${yLabs.join('')}
    <line x1="${XL}" y1="${YT}" x2="${XL}" y2="${YB}" stroke="#e5e7eb" stroke-width="1"/><line x1="${XL}" y1="${YB}" x2="${XR}" y2="${YB}" stroke="#e5e7eb" stroke-width="1"/>
    <text class="${NFC_CLS_AXIS_TITLE}" transform="rotate(-90, ${yAxisTitleX.toFixed(2)}, ${midY})" x="${yAxisTitleX.toFixed(2)}" y="${midY}" text-anchor="middle" dominant-baseline="middle"${nfcAxisTitleAttrs('Energy', H)}>Energy</text>
    <path d="${d}" stroke="#3b82f6" stroke-width="2" fill="none" stroke-linecap="round" vector-effect="non-scaling-stroke"/>
    ${xLabs.join('')}
    <text class="${NFC_CLS_AXIS_TITLE}" x="${midX}" y="${xAxisTitleY}" text-anchor="middle"${nfcAxisTitleAttrs('Age', W)}>Age</text>
  </svg>`;
}

function nutricodeExtensionInflammationLongevitySvg(idSuffix, svgId) {
  const categoryLabels = ['High', 'Med', 'Low'];
  const box = nutricodeExtChartPlotBox(categoryLabels);
  const { XL, XR, YT, YB, W, H, yLabelX, yAxisTitleX, xTickY, xAxisTitleY } = box;
  const xLow = XL + W * 0.12;
  const xMed = XL + W * 0.5;
  const xHigh = XL + W * 0.88;
  const yHi = YB - H * 0.88;
  const yMd = YB - H * 0.5;
  const yLo = YB - H * 0.12;
  const xSep1 = (xLow + xMed) / 2;
  const xSep2 = (xMed + xHigh) / 2;
  const ySep1 = (yHi + yMd) / 2;
  const ySep2 = (yMd + yLo) / 2;
  const aria = nutricodeEscAttr('Longevity expectation vs inflammation load: illustrative downward relationship.');
  const k = 3.4;
  const pts = [];
  const n = 32;
  for (let i = 0; i <= n; i++) {
    const u = i / n;
    const x = xLow + (xHigh - xLow) * u;
    const eased = (Math.exp(k * u) - 1) / (Math.exp(k) - 1);
    const y = yHi + (yLo - yHi) * eased;
    pts.push({ x, y });
  }
  const d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)} L ${pts.slice(1).map(p => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' L ')}`;
  const midX = ((XL + XR) / 2).toFixed(1);
  const midY = ((YT + YB) / 2).toFixed(2);
  return `<svg id="${svgId}" class="focus-extension-svg" viewBox="0 0 ${NFC_VB_W} ${NFC_VB_H}" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" role="img" aria-label="${aria}">
    <rect x="${XL}" y="${YT}" width="${W.toFixed(1)}" height="${H.toFixed(1)}" fill="none"/>
    <line x1="${xSep1.toFixed(1)}" y1="${YT}" x2="${xSep1.toFixed(1)}" y2="${YB}" stroke="#f0f0f2" stroke-width="1"/>
    <line x1="${xSep2.toFixed(1)}" y1="${YT}" x2="${xSep2.toFixed(1)}" y2="${YB}" stroke="#f0f0f2" stroke-width="1"/>
    <line x1="${XL}" y1="${ySep1.toFixed(1)}" x2="${XR}" y2="${ySep1.toFixed(1)}" stroke="#f0f0f2" stroke-width="1"/>
    <line x1="${XL}" y1="${ySep2.toFixed(1)}" x2="${XR}" y2="${ySep2.toFixed(1)}" stroke="#f0f0f2" stroke-width="1"/>
    <line x1="${XL}" y1="${YT}" x2="${XL}" y2="${YB}" stroke="#e5e7eb" stroke-width="1"/><line x1="${XL}" y1="${YB}" x2="${XR}" y2="${YB}" stroke="#e5e7eb" stroke-width="1"/>
    <text class="${NFC_CLS_TICK}" x="${yLabelX.toFixed(2)}" y="${(yHi + 3).toFixed(1)}" text-anchor="end">High</text>
    <text class="${NFC_CLS_TICK}" x="${yLabelX.toFixed(2)}" y="${(yMd + 3).toFixed(1)}" text-anchor="end">Med</text>
    <text class="${NFC_CLS_TICK}" x="${yLabelX.toFixed(2)}" y="${(yLo + 3).toFixed(1)}" text-anchor="end">Low</text>
    <text class="${NFC_CLS_AXIS_TITLE}" transform="rotate(-90, ${yAxisTitleX.toFixed(2)}, ${midY})" x="${yAxisTitleX.toFixed(2)}" y="${midY}" text-anchor="middle" dominant-baseline="middle"${nfcAxisTitleAttrs('Longevity Expectation', H)}>Longevity Expectation</text>
    <path d="${d}" stroke="#0f766e" stroke-width="2" fill="none" stroke-linecap="round" vector-effect="non-scaling-stroke"/>
    <text class="${NFC_CLS_TICK}" x="${xLow.toFixed(1)}" y="${xTickY}" text-anchor="middle">Low</text>
    <text class="${NFC_CLS_TICK}" x="${xMed.toFixed(1)}" y="${xTickY}" text-anchor="middle">Med</text>
    <text class="${NFC_CLS_TICK}" x="${xHigh.toFixed(1)}" y="${xTickY}" text-anchor="middle">High</text>
    <text class="${NFC_CLS_AXIS_TITLE}" x="${midX}" y="${xAxisTitleY}" text-anchor="middle"${nfcAxisTitleAttrs('Inflammation/Oxidative Stress', W)}>Inflammation/Oxidative Stress</text>
  </svg>`;
}

/* ==================================================================== */
/* Legend HTML + mount                                                    */
/* ==================================================================== */

/** Typography class set applied to every legend label span (matches ticks). */
const NFC_LEGEND_LABEL_CLASSES = 'font-jetbrains-mono font-jetbrains-mono-10px body-regular-grey';

function nutricodeFocusLegendItem(swatchClass, label) {
  const safeLabel = nutricodeEscapeHtmlText(label);
  return `<div class="nutricode-focus-card-mid__legend-item"><span class="nutricode-focus-card-mid__legend-swatch ${swatchClass}"></span><span class="nutricode-focus-card-mid__legend-label ${NFC_LEGEND_LABEL_CLASSES}">${safeLabel}</span></div>`;
}

function nutricodeFocusLegendHtml(chartType, chartData) {
  const ml = (chartData && chartData.metricLabel) || 'Metric';
  let items = '';
  if (chartType === 'cohort') {
    items = nutricodeFocusLegendItem('nutricode-focus-card-mid__legend-swatch--avg', 'Demographic Avg.');
  } else if (chartType === '7day_average') {
    items =
      nutricodeFocusLegendItem('nutricode-focus-card-mid__legend-swatch--deep', ml) +
      nutricodeFocusLegendItem('nutricode-focus-card-mid__legend-swatch--load', 'Training Load');
  } else if (chartType === 'single_correlation') {
    items = nutricodeFocusLegendItem('nutricode-focus-card-mid__legend-swatch--reg', 'Regression Line');
  } else if (chartType === 'healthspan') {
    items =
      `<div class="nutricode-focus-card-mid__legend-item"><span class="nutricode-focus-card-mid__legend-swatch" style="border-top:2px solid #6b7280"></span><span class="nutricode-focus-card-mid__legend-label ${NFC_LEGEND_LABEL_CLASSES}">Passive</span></div>` +
      `<div class="nutricode-focus-card-mid__legend-item"><span class="nutricode-focus-card-mid__legend-swatch" style="border-top:2px solid #10b981"></span><span class="nutricode-focus-card-mid__legend-label ${NFC_LEGEND_LABEL_CLASSES}">Proactive</span></div>`;
  } else if (chartType === 'systems') {
    items =
      `<div class="nutricode-focus-card-mid__legend-item"><span class="nutricode-focus-card-mid__legend-swatch" style="border-top:2px solid #ef4444"></span><span class="nutricode-focus-card-mid__legend-label ${NFC_LEGEND_LABEL_CLASSES}">Gut health</span></div>` +
      `<div class="nutricode-focus-card-mid__legend-item"><span class="nutricode-focus-card-mid__legend-swatch" style="border-top:2px solid #3b82f6"></span><span class="nutricode-focus-card-mid__legend-label ${NFC_LEGEND_LABEL_CLASSES}">Immunity</span></div>` +
      `<div class="nutricode-focus-card-mid__legend-item"><span class="nutricode-focus-card-mid__legend-swatch" style="border-top:2px solid #8b5cf6"></span><span class="nutricode-focus-card-mid__legend-label ${NFC_LEGEND_LABEL_CLASSES}">Metabolism</span></div>` +
      `<div class="nutricode-focus-card-mid__legend-item"><span class="nutricode-focus-card-mid__legend-swatch" style="border-top:2px solid #10b981"></span><span class="nutricode-focus-card-mid__legend-label ${NFC_LEGEND_LABEL_CLASSES}">Musculoskeletal</span></div>`;
  } else if (chartType === 'energy') {
    items = `<div class="nutricode-focus-card-mid__legend-item"><span class="nutricode-focus-card-mid__legend-swatch" style="border-top:2px solid #3b82f6"></span><span class="nutricode-focus-card-mid__legend-label ${NFC_LEGEND_LABEL_CLASSES}">Energy level</span></div>`;
  } else if (chartType === 'inflammation') {
    items = `<div class="nutricode-focus-card-mid__legend-item"><span class="nutricode-focus-card-mid__legend-swatch" style="border-top:2px solid #10b981"></span><span class="nutricode-focus-card-mid__legend-label ${NFC_LEGEND_LABEL_CLASSES}">Longevity outlook</span></div>`;
  } else {
    items = nutricodeFocusLegendItem('nutricode-focus-card-mid__legend-swatch--target', 'Target range');
  }
  return `<div class="nutricode-focus-card-mid__legend" aria-hidden="true">${items}</div>`;
}

function nutricodeMountFocusCardMid(container, options) {
  const el = typeof container === 'string' ? document.querySelector(container) : container;
  if (!el) return;
  const chartType = options.chart_type;
  const rawData = options.chart_data;
  const chartData =
    rawData != null && typeof rawData === 'object' && !Array.isArray(rawData) ? rawData : {};
  const caption = options.chart_caption != null ? String(options.chart_caption) : '';
  const domId = options.dom_id || 'nutricode-focus-card';
  const idSuffix = nutricodeSafeIdSuffix(domId);
  const svgId = `${idSuffix}-nfc-svg`;

  el.className = 'nutricode-focus-card-mid';
  el.setAttribute('data-chart-type', chartType);

  el.innerHTML = `
    <p class="nutricode-focus-card-mid__cap body-regular-grey font-uppercase-10px semiwide"></p>
    <div class="nutricode-focus-card-mid__chart-panel">
      <svg id="${svgId}" role="img" preserveAspectRatio="none"></svg>
    </div>
    ${nutricodeFocusLegendHtml(chartType, chartData)}
  `;
  const capEl = el.querySelector('.nutricode-focus-card-mid__cap');
  if (capEl) capEl.textContent = caption;

  const panel = el.querySelector('.nutricode-focus-card-mid__chart-panel');
  let svg = el.querySelector('svg');

  const staticTypes = ['healthspan', 'systems', 'energy', 'inflammation'];
  if (staticTypes.indexOf(chartType) >= 0) {
    let html = '';
    if (chartType === 'healthspan') html = nutricodeHealthspanLifespanVsHealthspanSvg(idSuffix, svgId);
    else if (chartType === 'systems') html = nutricodeExtensionSystemDeclineSvg(idSuffix, svgId);
    else if (chartType === 'energy') html = nutricodeExtensionEnergyOverAgeSvg(idSuffix, svgId);
    else if (chartType === 'inflammation') html = nutricodeExtensionInflammationLongevitySvg(idSuffix, svgId);
    panel.innerHTML = html;
    svg = panel.querySelector('svg');
    if (svg && !svg.getAttribute('preserveAspectRatio')) svg.setAttribute('preserveAspectRatio', 'none');
  } else if (chartType === 'cohort') {
    nutricodeDrawFocusRem(svg, chartData, idSuffix);
  } else if (chartType === '7day_average') {
    nutricodeDrawFocusDeepInt(svg, chartData, idSuffix);
  } else if (chartType === 'variance') {
    nutricodeDrawFocusHrv(svg, chartData, idSuffix);
  } else if (chartType === 'single_correlation') {
    nutricodeDrawFocusScatter(svg, chartData, idSuffix);
  } else {
    nutricodeDrawFocusHrv(svg, chartData, idSuffix);
  }

  if (svg) nfcBindFontPx(svg);
  nfcScheduleLegendEqualize();
}

/**
 * Equalize legend heights across every `.nutricode-focus-card-mid__legend`
 * currently in the DOM so the whole focus strip keeps the same total card
 * height even when individual legends wrap onto different numbers of rows.
 *
 * Strategy:
 *  1. Clear any previously-written `min-height` so we measure the natural
 *     (content-driven) height.
 *  2. Read each legend's `getBoundingClientRect().height` and take the max.
 *  3. Write that max back as `min-height` on every legend.
 *
 * Re-runs are coalesced into one rAF so multiple mounts in the same tick,
 * plus any follow-up resize, produce a single pass.
 */
let __nfcEqualizeRaf = 0;

function nfcEqualizeLegendHeights() {
  if (typeof document === 'undefined') return;
  const legends = document.querySelectorAll('.nutricode-focus-card-mid__legend');
  if (!legends.length) return;
  legends.forEach((el) => {
    el.style.minHeight = '';
  });
  let maxH = 0;
  legends.forEach((el) => {
    const h = el.getBoundingClientRect().height;
    if (h > maxH) maxH = h;
  });
  if (!(maxH > 0)) return;
  const value = Math.ceil(maxH) + 'px';
  legends.forEach((el) => {
    el.style.minHeight = value;
  });
}

function nfcScheduleLegendEqualize() {
  if (typeof window === 'undefined') return;
  if (__nfcEqualizeRaf) cancelAnimationFrame(__nfcEqualizeRaf);
  __nfcEqualizeRaf = requestAnimationFrame(() => {
    __nfcEqualizeRaf = 0;
    nfcEqualizeLegendHeights();
  });
}

if (typeof window !== 'undefined' && !window.__nfcLegendResizeBound) {
  window.__nfcLegendResizeBound = true;
  window.addEventListener('resize', nfcScheduleLegendEqualize, { passive: true });
}

const NutricodeFocusCharts = {
  mount: nutricodeMountFocusCardMid,
  escAttr: nutricodeEscAttr,
  safeIdSuffix: nutricodeSafeIdSuffix,
  equalizeLegendHeights: nfcEqualizeLegendHeights,
};

if (typeof window !== 'undefined') window.NutricodeFocusCharts = NutricodeFocusCharts;
if (typeof module !== 'undefined' && module.exports) module.exports = NutricodeFocusCharts;
